import { exchangeIDtokenForAccessToken, udtLoginRequest } from '@services'
import { ALLOW_COOKIES, cookieState } from '@composables/useStorage.js'
import { globalUpdateFns } from '@composables/useBrowse.js'
import useFetchSubs from '@composables/useFetchSubs.js'
import { isString, isObj } from '@helpers/utils.js'
import useFilter from '@composables/useFilter.js'
import useSearch from '@composables/useSearch.js'
import { KEYS_MODS } from '@config/storeKeys.js'
import useRoute from '@composables/useRoute.js'
import { CSS_ID } from '@config/htmlIDs.js'
import i18n from '@plugins/localisation.js'
import { gameStore, store } from '@stores'
import { MIN_PAGE_HEIGHT } from '@config'
import { readonly, ref } from 'vue'
import DOMPurify from 'dompurify'
import {
  setFilterSetting,
  filterSettings,
  setAuthOption,
  ALLOWED_URLS,
  setSettings,
  AuthOptions,
  setStyle,
  Settings,
  HOST_URL,
  HEADERS,
  Style,
} from '@config/game.js'

export const authState = ref({})
const scrollState = ref({ frameTop: 0, scrollY: 0 })
export const containerScroll = readonly(scrollState)
export const containerHeight = ref(0)
export const hostUrl = ref(location.href.split('#')[0])
export const windowHeight = ref(MIN_PAGE_HEIGHT)

export function sendMessage(msg) {
  window.parent.postMessage(msg, HOST_URL)
}

const onScroll = {}

export default function () {
  window.addEventListener('message', (e) => {
    if (
      (!import.meta.env.DEV && !ALLOWED_URLS.includes(e.origin)) ||
      !e.data ||
      !isObj(e.data)
    )
      return

    const {
      modioSubscriptions,
      modioAllowCookies,
      modioPurchases,
      modioSettings,
      modioPlatform,
      modioWindow,
      modioFilter,
      modioPortal,
      modioScroll,
      modioQuery,
      modioStyle,
      modioAuth,
      modioLang,
      modioHome,
      modioUrl,
      modioCSS,
    } = e.data

    if (modioUrl) {
      hostUrl.value = modioUrl.split('#')[0]
    }

    if (Number.isInteger(modioWindow)) {
      windowHeight.value = modioWindow
    }

    if (modioStyle) {
      Object.keys(Style.value).forEach((key) => setStyle(key, modioStyle[key]))
    }

    if (modioCSS) {
      let elem = document.getElementById(CSS_ID)
      if (!elem) {
        elem = document.createElement('style')
        document.getElementsByTagName('head')[0].appendChild(elem)
      }
      elem.innerHTML = DOMPurify.sanitize(modioCSS)
    }

    if (modioAllowCookies !== undefined) {
      const allow = ['1', 1, 'true', true].includes(modioAllowCookies)
      cookieState.value = allow ? ALLOW_COOKIES : 0
    }

    if (modioLang) {
      i18n.global.locale.value = modioLang
    }

    if (modioSettings) {
      Object.keys(Settings.value).forEach((key) =>
        setSettings(key, modioSettings[key])
      )

      Object.keys(AuthOptions.value).forEach((key) =>
        setAuthOption(key, modioSettings[key])
      )
    }

    if (modioFilter) {
      Object.keys(filterSettings.value).forEach((key) =>
        setFilterSetting(key, modioFilter[key])
      )
    }

    if (modioScroll) {
      scrollState.value = modioScroll
      Object.values(onScroll).forEach((fn) => fn())
    }

    if (modioPortal) {
      HEADERS.portal = modioPortal
    }

    if (modioPlatform) {
      HEADERS.platform = modioPlatform
    }

    if (modioAuth) {
      const { id_token, udt, email } = modioAuth
      if (id_token) {
        exchangeIDtokenForAccessToken(id_token, email)
      } else if (udt) {
        udtLoginRequest(udt, true)
      }
    }

    if (modioQuery) {
      const game = gameStore().getPublicGame()
      const userNameId = useRoute().getUserNameId().value

      const id = store().generateId(
        KEYS_MODS,
        `${game.value.name_id}${userNameId || ''}`
      )
      // isString to accept empty string as valid
      if (isString(modioQuery._q)) {
        const { clearSearch } = useSearch(id, modioQuery._q)
        if (!modioQuery._q) {
          clearSearch()
        }
        globalUpdateFns[id]?.('search')
      }
      if (isString(modioQuery.tags)) {
        const { filter, resetFilter, updateFilter } = useFilter(id)
        const tag = modioQuery.tags.toLowerCase()

        if (tag === '') {
          resetFilter()
          return
        }

        const option = filter.value.find((o) =>
          o.tags.some((t) => t.toLowerCase() === tag)
        )
        if (option) {
          updateFilter({
            id: option.id,
            type: option.type,
            value: modioQuery.tags,
          })
        }
      }
    }

    if (modioSubscriptions) {
      useFetchSubs().fetchSubs(true)
    }

    if (modioPurchases) {
      useFetchSubs().fetchPurchases(true)
    }

    if (modioHome) {
      useRoute().push({ path: '/' })
    }
  })

  sendMessage({ modioInit: true })
}

export function subscribeToScroll(id, callback) {
  onScroll[id] = callback
}

export function unsubscribeFromScroll(id) {
  delete onScroll[id]
}
